import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectDatesData, WorkflowOrganisation } from "../../../../../../../../../models";
import { searchProjectOrganisations, searchWorkflowTemplateTasks } from "../../../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../../../service/Shared";
import { applyWorkflowTemplate } from "../../../../../../../../../service/workflow/WorkflowService.full";
import { useAuth } from "../../../../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../../../../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../../../../../../../../widget";

interface UseWorkflowPermissionsReturnData {
  workflowOrganisations: WorkflowOrganisation[];
  isApplyWorkflowTemplateLoading: boolean;
  showOwnerOrganisationTasksWarning: boolean;
  handleClose: () => void;
  handleApplyWorkflowTemplate: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateOrganisationAccessLevel: (uuid: string, accessLevel: string) => void;
}

interface UseWorkflowPermissionsProps {
  workflowTemplateUuid: string | undefined;
  selectedProject: ProjectDatesData | undefined;
  onClose: () => void;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
}

export const useWorkflowPermissions = ({
  workflowTemplateUuid,
  selectedProject,
  onClose,
  setErrors,
}: UseWorkflowPermissionsProps): UseWorkflowPermissionsReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType } = useAuth();
  const [workflowOrganisations, setWorkflowOrganisations] = useState<WorkflowOrganisation[]>([]);
  const [isApplyWorkflowTemplateLoading, setIsApplyWorkflowTemplateLoading] = useState(false);
  const [showOwnerOrganisationTasksWarning, setShowOwnerOrganisationTasksWarning] = useState(false);

  const handleClose = (): void => {
    setWorkflowOrganisations([]);
    setShowOwnerOrganisationTasksWarning(false);
    setErrors([]);
    onClose();
  };

  const handleUpdateOrganisationAccessLevel = (uuid: string, accessLevel: string): void => {
    setWorkflowOrganisations(
      workflowOrganisations.map((org) => {
        if (org.uuid === uuid) {
          return { ...org, accessLevel };
        }
        return org;
      })
    );
  };

  // BR-02117 if there are any tasks that have an owner org that isn't assigned to the template, show warning
  const checkTaskOrganisationVisibility = async (filteredOrganisations: WorkflowOrganisation[]): Promise<boolean> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.templateUuid = {
      operator: "eq",
      value: workflowTemplateUuid,
    };

    filterCriteria.ownerOrganisationType = {
      operator: "nin",
      value: filteredOrganisations.map((org) => org.role),
    };

    const response = await searchWorkflowTemplateTasks({
      paging: { beforeCursor: null, afterCursor: null, limit: 1 },
      filter: { results: filterCriteria },
    });

    if (response.status === Status.Success && response.data && response.data.results.length > 0) {
      setShowOwnerOrganisationTasksWarning(true);
      return false;
    }
    return true;
  };

  const handleApplyWorkflowTemplate = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (selectedProject === undefined || workflowTemplateUuid === undefined) return;
    setErrors([]);
    const filteredOrganisations = workflowOrganisations.filter(
      (org) => org.accessLevel !== undefined && org.accessLevel !== "No access"
    );

    if (!showOwnerOrganisationTasksWarning) {
      if (!(await checkTaskOrganisationVisibility(filteredOrganisations))) return;
    }

    const applyWorkflowTemplateOrganisations = filteredOrganisations.map((org) => ({
      accessLevel: org.accessLevel as string,
      organisationUuid: org.uuid,
    }));

    const response = await applyWorkflowTemplate({
      objectUuid: selectedProject.uuid,
      workflowTemplateUuid,
      organisations: applyWorkflowTemplateOrganisations,
    });

    if (response.status === Status.Success && response.data) {
      Toast.success({ message: "Workflow template successfully applied" });
      navigate(getWorkflowTasksBoardRoute(response.data.workflowUuid, currentUserType));
      setErrors([]);
    }
    if (response.status === Status.Error) {
      setErrors(response.errors);
    }
  }, setIsApplyWorkflowTemplateLoading);

  const getAndSetWorkflowOrganisations = useCallback(
    async (projectUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.results = {
        role: {
          operator: "in",
          value: "Asset Manager, Developer, Development Manager, Guest - Write, Guest - Read",
        },
      };

      const response = await searchProjectOrganisations({
        projectUuid,
        paging: { beforeCursor: null, afterCursor: null, limit: 10 },
        filter: filterCriteria,
      });

      if (response.status === Status.Success && response.data && response.data?.results.length > 0) {
        setWorkflowOrganisations(
          response.data.results
            .map(
              (org) =>
                ({
                  uuid: org.organisationUuid,
                  displayName: org.displayName,
                  role: org.role === "Guest - Read" || org.role === "Guest - Write" ? "Guest Developer" : org.role,
                  accessLevel: org.organisationUuid === currentOrganisationUuid ? "Owner" : undefined,
                }) as WorkflowOrganisation
            )
            .sort((a, b) => (b.accessLevel === "Owner" ? 1 : 0) - (a.accessLevel === "Owner" ? 1 : 0))
        );
      }
    },
    [selectedProject?.uuid]
  );

  useEffect(() => {
    if (selectedProject !== undefined) {
      getAndSetWorkflowOrganisations(selectedProject.uuid);
    }
  }, [selectedProject?.uuid]);

  return {
    showOwnerOrganisationTasksWarning,
    workflowOrganisations,
    handleUpdateOrganisationAccessLevel,
    isApplyWorkflowTemplateLoading,
    handleApplyWorkflowTemplate,
    handleClose,
  };
};
